import React, { useState } from 'react';
import { Collection } from '../types';

export interface FilterOptions {
  priceRange: [number, number];
  collections: number[];
  sizes: string[];
  availability: 'all' | 'in_stock' | 'low_stock';
  sortBy: 'newest' | 'price_low' | 'price_high' | 'popularity';
}

interface ProductFiltersProps {
  collections: Collection[];
  availableSizes: string[];
  filters: FilterOptions;
  onFiltersChange: (_filters: FilterOptions) => void;
  onReset: () => void;
  minPrice?: number;
  maxPrice?: number;
}

const ProductFilters: React.FC<ProductFiltersProps> = ({
  collections,
  availableSizes,
  filters,
  onFiltersChange,
  onReset,
  minPrice = 0,
  maxPrice = 1000000,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handlePriceChange = (index: 0 | 1, value: number) => {
    const newRange: [number, number] = [...filters.priceRange] as [number, number];
    newRange[index] = value;
    onFiltersChange({ ...filters, priceRange: newRange });
  };

  const handleCollectionToggle = (collectionId: number) => {
    const newCollections = filters.collections.includes(collectionId)
      ? filters.collections.filter(id => id !== collectionId)
      : [...filters.collections, collectionId];
    onFiltersChange({ ...filters, collections: newCollections });
  };

  const handleSizeToggle = (size: string) => {
    const newSizes = filters.sizes.includes(size)
      ? filters.sizes.filter(s => s !== size)
      : [...filters.sizes, size];
    onFiltersChange({ ...filters, sizes: newSizes });
  };

  const handleAvailabilityChange = (availability: FilterOptions['availability']) => {
    onFiltersChange({ ...filters, availability });
  };

  const handleSortChange = (sortBy: FilterOptions['sortBy']) => {
    onFiltersChange({ ...filters, sortBy });
  };

  const activeFiltersCount =
    filters.collections.length +
    filters.sizes.length +
    (filters.availability !== 'all' ? 1 : 0) +
    (filters.priceRange[0] !== minPrice || filters.priceRange[1] !== maxPrice ? 1 : 0);

  return (
    <div className="mb-8">
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-4 flex gap-4">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1 flex items-center justify-center gap-2 bg-canvas-primary text-fg-primary border-2 border-stroke-primary px-6 py-3 rounded-xl font-medium transition-all duration-300 hover:border-fg-highlight-primary"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filtros {activeFiltersCount > 0 && `(${activeFiltersCount})`}
        </button>

        {/* Sort Dropdown for Mobile */}
        <select
          value={filters.sortBy}
          onChange={(e) => handleSortChange(e.target.value as FilterOptions['sortBy'])}
          className="flex-1 bg-canvas-primary text-fg-primary border-2 border-stroke-primary px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:border-fg-highlight-primary focus:outline-none focus:border-fg-highlight-primary"
        >
          <option value="newest">Más recientes</option>
          <option value="price_low">Precio: Bajo a Alto</option>
          <option value="price_high">Precio: Alto a Bajo</option>
          <option value="popularity">Más populares</option>
        </select>
      </div>

      {/* Desktop Sort */}
      <div className="hidden lg:flex justify-between items-center mb-6">
        <div className="flex items-center gap-2 text-fg-secondary">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
          </svg>
          <span className="font-medium">Ordenar por:</span>
        </div>
        <div className="flex gap-3">
          {[
            { value: 'newest', label: 'Más recientes' },
            { value: 'price_low', label: 'Precio ↑' },
            { value: 'price_high', label: 'Precio ↓' },
            { value: 'popularity', label: 'Populares' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleSortChange(option.value as FilterOptions['sortBy'])}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                filters.sortBy === option.value
                  ? 'bg-fg-highlight-primary text-white shadow-md'
                  : 'bg-canvas-primary text-fg-secondary hover:bg-canvas-secondary border border-stroke-primary'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters Panel */}
      <div className={`${isOpen ? 'block' : 'hidden'} lg:block`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 bg-canvas-primary p-6 rounded-2xl shadow-md border border-stroke-primary">
          {/* Price Range */}
          <div className="space-y-3">
            <h3 className="font-bold text-fg-primary flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Rango de Precio
            </h3>
            <div className="space-y-2">
              <input
                type="range"
                min={minPrice}
                max={maxPrice}
                value={filters.priceRange[0]}
                onChange={(e) => handlePriceChange(0, Number(e.target.value))}
                className="w-full accent-fg-highlight-primary"
              />
              <input
                type="range"
                min={minPrice}
                max={maxPrice}
                value={filters.priceRange[1]}
                onChange={(e) => handlePriceChange(1, Number(e.target.value))}
                className="w-full accent-fg-highlight-primary"
              />
              <div className="flex justify-between text-sm text-fg-secondary">
                <span>${filters.priceRange[0].toLocaleString()}</span>
                <span>${filters.priceRange[1].toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Collections */}
          <div className="space-y-3">
            <h3 className="font-bold text-fg-primary flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Colecciones
            </h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {collections.map((collection) => (
                <label key={collection.id} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filters.collections.includes(collection.id)}
                    onChange={() => handleCollectionToggle(collection.id)}
                    className="w-4 h-4 accent-fg-highlight-primary cursor-pointer"
                  />
                  <span className="text-sm text-fg-secondary group-hover:text-fg-primary transition-colors">
                    {collection.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Sizes */}
          {availableSizes.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-bold text-fg-primary flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                Tallas
              </h3>
              <div className="flex flex-wrap gap-2">
                {availableSizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => handleSizeToggle(size)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                      filters.sizes.includes(size)
                        ? 'bg-fg-highlight-primary text-white shadow-md'
                        : 'bg-canvas-secondary text-fg-secondary hover:bg-canvas-tertiary border border-stroke-primary'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Availability */}
          <div className="space-y-3">
            <h3 className="font-bold text-fg-primary flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Disponibilidad
            </h3>
            <div className="space-y-2">
              {[
                { value: 'all', label: 'Todos', icon: '🔄' },
                { value: 'in_stock', label: 'En stock', icon: '✅' },
                { value: 'low_stock', label: 'Stock bajo', icon: '⚠️' },
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name="availability"
                    checked={filters.availability === option.value}
                    onChange={() => handleAvailabilityChange(option.value as FilterOptions['availability'])}
                    className="w-4 h-4 accent-fg-highlight-primary cursor-pointer"
                  />
                  <span className="text-sm text-fg-secondary group-hover:text-fg-primary transition-colors">
                    {option.icon} {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Reset Filters */}
        {activeFiltersCount > 0 && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={onReset}
              className="flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Limpiar filtros ({activeFiltersCount})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductFilters;
